import { Button, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import type { Organization } from '@eppendorf/vnls-user-tenant-utils';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBlocker, useSearchParams } from 'react-router-dom';
import { SIDECAR_ROUTE_PARAMS } from 'src/sidecar-route-params';
import { ConfirmUnsavedChangesDialog } from '$components/confirm-unsaved-changes-dialog';
import { useFeatureTogglesContext } from '$context/feature-toggles-context';
import useOrganizationDetailStore from '$context/organization-detail-context';
import { DeleteOrganizationDialog } from '$features/organizations/delete-organization-dialog';
import OrganizationActions from '$features/organizations/detail/detail-tab/actions/actions';
import { OrganizationInfo } from '$features/organizations/detail/detail-tab/info/organization-info';
import { OrganizationForm, OrganizationFormId, OrganizationFormMappers, type OrganizationFormProfile } from '$features/organizations/form';
import { useGetOrganization, useUpdateOrganization } from '$features/organizations/organizations.api';
import { useIsSupportUser } from '$hooks/use-is-support-user.hook';
import { useQueryString } from '$shared/custom-hooks/use-query-string';
import './organization-detail.scss';

export function OrganizationDetail() {
  const { t } = useTranslation();
  const { isFeatureEnabled } = useFeatureTogglesContext();
  const deleteFeatureEnabled = isFeatureEnabled('ESEVNLS_5067_DELETE_ORGANIZATION');
  const [search] = useSearchParams();
  const { isEditMode, setIsDirty, setIsEditMode } = useOrganizationDetailStore();
  const [showSaveUnchangedValues, setShowSaveUnchangedValues] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { setQueryParams } = useQueryString<{ sidecarRoute: string; id: string }>();
  const subscriptionNames = ['Essentials', 'SampleSafetyPlus'];
  const typeNames = ['Academia', 'Industry'];

  const { mutateAsync, isPending } = useUpdateOrganization();

  const { data: organization } = useGetOrganization(search.get(SIDECAR_ROUTE_PARAMS.organization.id) || '');
  const isKeylightManaged = !!organization?.keylightContractId;
  const isSupportUser = useIsSupportUser(deleteFeatureEnabled || isKeylightManaged);
  const canDeleteOrganization = deleteFeatureEnabled && isSupportUser;
  const tenantFieldsDisabled = isKeylightManaged && !isSupportUser;

  const formValues = useMemo(
    () => (organization ? OrganizationFormMappers.mapOrganizationToFormValues(organization as Organization) : undefined),
    [organization],
  );

  const organizationForm = useForm<OrganizationFormProfile>({
    values: formValues,
  });

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
  } = organizationForm;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  );

  const handleToggleEdit = () => {
    if (isEditMode && isDirty) {
      setShowSaveUnchangedValues(true);
      return;
    }

    setIsEditMode(!isEditMode);
  };

  const handleEditOrganization = async (formValues: OrganizationFormProfile) => {
    if (!organization) return;

    try {
      await mutateAsync(
        tenantFieldsDisabled
          ? { id: organization.id, additionalFeatures: formValues.additionalFeatures }
          : OrganizationFormMappers.mapFormValuesToUpdateRequest(organization.id, formValues),
      );
      setIsEditMode(false);
      setShowSaveUnchangedValues(false);
      reset();
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        organizationForm.setError('adminEmail', {
          type: 'conflict',
          message: t('addOrganization.adminUserAlreadyExists'),
        });
      }
    }
  };

  const handleSaveChanges = () => {
    handleSubmit(async (formValues) => {
      await handleEditOrganization(formValues);
    })();

    blocker.proceed?.();

    setShowSaveUnchangedValues(false);
  };

  const handleDiscardChanges = () => {
    setIsEditMode(false);
    blocker.proceed?.();
    setShowSaveUnchangedValues(false);
    reset();
  };

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  if (!organization) {
    return null;
  }

  return (
    <div>
      {isEditMode ? (
        <>
          {isKeylightManaged && (
            <div className="organization-keylight-warning m-bottom-l" role="alert">
              <Icon name="warning" size={IconSizeClasses.Small} className="organization-keylight-warning__icon" />
              <span>
                {t('organizationDetails.keylightEditWarning')}
                {isSupportUser && (
                  <>
                    <br />
                    {t('organizationDetails.keylightEditCaution')}
                  </>
                )}
              </span>
            </div>
          )}
          {/* eslint-disable-next-line react/jsx-props-no-spreading -- FormProvider does not privde a prop to give the whole form context */}
          <FormProvider {...organizationForm}>
            <OrganizationForm
              subscriptions={subscriptionNames}
              types={typeNames}
              additionalFeatures={['REPORT_HUB_ACCESS']}
              tenantFieldsDisabled={tenantFieldsDisabled}
              onSubmit={handleEditOrganization}
            />
          </FormProvider>

          <div className="flex flex__jc--flex-end" id="org-detail-edit-button">
            <Button disabled={isPending} variant="secondary" onClick={handleToggleEdit}>
              {t('shared.cancel')}
            </Button>
            <Button type="submit" className="m-left-l btn--solid" formId={OrganizationFormId} disabled={isPending || !isDirty}>
              {t('shared.save')}
            </Button>
          </div>
        </>
      ) : (
        <>
          <OrganizationInfo organization={organization as Organization} />
          <OrganizationActions
            organization={organization as Organization}
            onEditPress={handleToggleEdit}
            canDeleteOrganization={canDeleteOrganization}
            onDeletePress={() => setIsDeleteDialogOpen(true)}
          />
        </>
      )}

      <ConfirmUnsavedChangesDialog
        isOpen={blocker.state === 'blocked' || showSaveUnchangedValues}
        onSave={handleSaveChanges}
        onDiscard={handleDiscardChanges}
      />
      {canDeleteOrganization && isDeleteDialogOpen && (
        <DeleteOrganizationDialog
          organization={organization}
          onClose={() => setIsDeleteDialogOpen(false)}
          onDeleted={() => setQueryParams({ sidecarRoute: '', id: '' })}
        />
      )}
    </div>
  );
}
